import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation, createPath } from "react-router-dom";
import {
  ComponentProjectListRequestPageInitialState,
  CpntPjtInfo,
  SpreadsheetState,
  usePageState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import { useGetComponentProjectsDeployCandidate } from "../../services/component-projects";
import {
  useGetEdhProtocolList,
  useGetGeneralCodes,
} from "../../services/data-list";
import { useUpdateComponentAttributeTicketStatuses } from "../../services/tickets";
import { EvntGrp } from "../../util/general-code/enums/evnt-grp";
import {
  DeploymentRequestType,
  InDefCommonFlagType,
  TcktStsTypeCpntDsgn,
  TcktType,
} from "../../util/general-code/enums/general-codes";
import { Option } from "../../util/option";
import { mappingErrorResponseToSpreadsheet } from "../../util/spreadsheet-data-utils";
import { getMergedEdhProtocolValue } from "../component-deployment-request-list/ComponentDeploymentRequestListConstant";
import {
  ComponentMap,
  DeploymentRequestInfomationInputDialogContent,
  DeploymentRequestInfomationInputDialogInitialState,
  DeploymentRequestInfomationInputDialogState,
  useDeploymentRequestInfomationInputDialogState,
} from "../dialog/deployment-request-infomation-input-dialog";
import {
  REQUEST_CD_TYPES,
  requestCdTypes,
  toBoolean,
} from "./ComponentProjectListRequestConstant";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const ComponentProjectListRequestPageContent = () => {
  const { showConfirmationDialog, showToast, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const navigate = useNavigate();
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    envType: queryParameter.get("envType") ?? "",
    dipByRgn: queryParameter.get("dipByRgn") ?? "",
    cpntId: queryParameter.get("cpntId") ?? "",
  };
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [
    deploymentRequestInfomationInputDialogState,
    setDeploymentRequestInfomationInputDialogState,
  ] = useDeploymentRequestInfomationInputDialogState();
  const [getGeneralCodesResponse, sendGeneralCodesRequest] =
    useGetGeneralCodes();
  const [
    getComponentProjectsDeployCandidateResponse,
    sendComponentProjectsDeployCandidateRequest,
  ] = useGetComponentProjectsDeployCandidate();
  const [getEdhProtocolListResponse, sendGetEdhProtocolListRequest] =
    useGetEdhProtocolList();
  const [
    getUpdateComponentAttributeTicketStatusesReponse,
    sendUpdateComponentAttributeTicketStatusesRequest,
  ] = useUpdateComponentAttributeTicketStatuses();

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const EVENT_CLICK_PJT_NAME = "pjtName";
  const EVENT_CLICK_CPNT_ID = "cpntId";
  const EVENT_CLICK_PJT_NAME_NEW_TAB_ICON = "pjtName_newTabIcon";
  const EVENT_CLICK_CPNT_ID_NEW_TAB_ICON = "cpntId_newTabIcon";
  const EVENT_EDIT_CHECK = "isChecked";

  const COMPONENT_PROJECT_LIST_REQUEST_TEMPLATE_FILE_URL =
    "spreadsheet-template/component-project-list-deploy-request.xlsx";

  const spreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    const cpntPjtList = pageState.cpntPjtList;
    const generalCode = pageState.generalCode;
    return {
      envType: params.envType,
      cpntPjtList: cpntPjtList,
      generalCode: generalCode,
    };
  }, [params.envType, pageState.cpntPjtList, pageState.generalCode]);

  const onInitialDisplayed = () => {
    resetUserError();
    sendGeneralCodesRequest({ cdTypes: requestCdTypes });
    sendComponentProjectsDeployCandidateRequest({
      envType: params.envType,
      dipByRgn: params.dipByRgn,
      cpntId: params.cpntId,
    });
    sendGetEdhProtocolListRequest({});
    setAppState({
      ...appState,
      title: `Component Deployment Request Create (${params.envType})`,
    });
  };

  const onGetGeneralCodesResponse = () => {
    let inDefCommonFlagType = [] as Option[];
    let tcktStsTypeCpntPrjPrgrs = [] as Option[];
    const GeneralCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];
    if (GeneralCode.length === 0) {
      return;
    }

    GeneralCode.map((e) => {
      switch (e.cdType) {
        case REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE:
          inDefCommonFlagType = e.cdList;
          break;
        case REQUEST_CD_TYPES.TCKT_STS_TYPE_CPNT_PRJ_PRGRS:
          tcktStsTypeCpntPrjPrgrs = e.cdList;
          break;
      }
    });
    setPageState((prevState) => ({
      ...prevState,
      generalCode: {
        ...prevState.generalCode,
        inDefCommonFlagType: inDefCommonFlagType,
        tcktStsTypeCpntPrjPrgrs: tcktStsTypeCpntPrjPrgrs,
      },
    }));
  };

  const onGetComponentProjectsDeployCandidateResponse = () => {
    if (!getComponentProjectsDeployCandidateResponse) {
      return;
    }
    const componentProjectList =
      getComponentProjectsDeployCandidateResponse.cpntPjtList;

    const cpntPjtList: CpntPjtInfo[] = componentProjectList.map((e, idx) => ({
      idx: idx,
      isEdited: false,
      isChecked: false,
      message: "",
      cpntPjt: e,
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    }));
    setPageState((prevState) => ({
      ...prevState,
      cpntPjtList: cpntPjtList,
    }));
  };

  const onGetEdhProtocolListResponse = () => {
    if (!getEdhProtocolListResponse) return;

    const edhProtocolList = getEdhProtocolListResponse.edhProtocolList.map(
      (e) => ({
        label: e.edhPrtclDsplyName,
        value: getMergedEdhProtocolValue(e.inOutTypeCd, e.edhPrtclTypeCd),
      }),
    );

    setPageState((prevState) => ({
      ...prevState,
      generalCode: {
        ...prevState.generalCode,
        edhProtocolList: edhProtocolList,
      },
    }));
  };

  const onGetUpdateComponentAttributeTicketStatusesReponse = () => {
    if (!!getUpdateComponentAttributeTicketStatusesReponse) {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      transition("/component-project-list");
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesResponse, [getGeneralCodesResponse]);
  useEffect(onGetComponentProjectsDeployCandidateResponse, [
    getComponentProjectsDeployCandidateResponse,
  ]);
  useEffect(onGetEdhProtocolListResponse, [getEdhProtocolListResponse]);
  useEffect(onGetUpdateComponentAttributeTicketStatusesReponse, [
    getUpdateComponentAttributeTicketStatusesReponse,
  ]);

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const transitionHandle = (_event: CustomEvent) => {
    if (!pageState.isEdited) {
      navigate(-1);
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked: () => {
        navigate(-1);
      },
    });
  };

  const onClickConfirmDeploymentRequest = () => {
    if (!spreadsheetState) return;
    const componentMap: ComponentMap[] = [];
    spreadsheetState.cpntPjtList.forEach((e, index) => {
      if (e.isChecked) {
        const component: ComponentMap = {
          index: index,
          pjtId: e.cpntPjt.pjtId ?? "",
          tcktId: e.cpntPjt.tcktId ?? "",
        };
        componentMap.push(component);
      }
    });

    if (componentMap?.length == 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }

    const dialogState: DeploymentRequestInfomationInputDialogState = {
      ...DeploymentRequestInfomationInputDialogInitialState,
      dplymntReqType: DeploymentRequestType.COMPONENT,
      processTyp: params.envType,
      deploymentRequestList: [],
      componentMap,
      isShow: true,
      onCreateComponentDeploymentRequestError: (
        createComponentDeploymentRequest,
      ) => {
        if (!Array.isArray(createComponentDeploymentRequest)) return;
        const currentrecords: CpntPjtInfo[] = spreadsheetState
          ? [...spreadsheetState.cpntPjtList]
          : [];

        const newSpreadsheetRecordData: CpntPjtInfo[] =
          mappingErrorResponseToSpreadsheet(
            createComponentDeploymentRequest,
            currentrecords,
            ComponentProjectListRequestPageInitialState.displayColumn,
          );

        if (!spreadsheetState) return;
        setSpreadsheetState({
          ...spreadsheetState,
          cpntPjtList: newSpreadsheetRecordData,
        });
      },
    };
    setDeploymentRequestInfomationInputDialogState(dialogState);
  };

  const onClickReEnterAttributes = () => {
    if (!spreadsheetState) return;

    const targetValue = spreadsheetState.cpntPjtList.filter((e) => e.isChecked);

    if (targetValue.some((row) => !row.cpntPjt.cpntId)) return;

    if (targetValue?.length == 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: () => {
        sendUpdateComponentAttributeTicketStatusesRequest({
          updateCpntTcktList: targetValue.map((row) => ({
            idx: row.idx,
            pjtId: row.cpntPjt.pjtId as string,
            evntGrpTypeCd: EvntGrp.EVNT_GRP_PT_010_006,
            tcktTypeTypeCd: TcktType.COMPONENT_DESIGN,
            tcktStsTypeCd: TcktStsTypeCpntDsgn.DESIGN,
            cmnt: "",
          })),
        });
      },
    });
  };

  const onCellEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const eventName: string = event.detail.name;
    const value = event.detail.cell.value;
    const isChanged = event.detail.cell.row.isChanged;
    let spreadsheetData: SpreadsheetState = JSON.parse(
      JSON.stringify(spreadsheetState),
    );

    if (eventName === EVENT_EDIT_CHECK) {
      const targetCpntPjt: CpntPjtInfo = event.detail.value;
      const newCpntPjtList: CpntPjtInfo[] = spreadsheetState.cpntPjtList.map(
        (e) =>
          e.cpntPjt.pjtId === targetCpntPjt.cpntPjt.pjtId
            ? { ...e, isChecked: toBoolean(value), isEdited: isChanged }
            : e,
      );
      setSpreadsheetState({ ...spreadsheetData, cpntPjtList: newCpntPjtList });
      setPageState((prevState) => ({
        ...prevState,
        isEdited: true,
      }));
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newEdhProtocolList = _.cloneDeep(
      spreadsheetState?.cpntPjtList,
    ) as CpntPjtInfo[];

    const dataNum = newEdhProtocolList.map((item, index) =>
      item.cpntPjt.cpntAttrs.length > 0 ? item.cpntPjt.cpntAttrs.length : 1,
    );
    let currentParentIdx = 0;
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.row.index - 2;
      let parentIdx = 0;
      let countParent = 0;
      let rowSums = 0;
      // there is used union cells in spreadsheet, so need to calculate parent index
      while (rowIdx >= rowSums) {
        rowSums += dataNum[countParent];
        countParent++;
      }
      parentIdx = countParent - 1;
      if (currentParentIdx === parentIdx && rowIdx !== parentIdx) return; // skip if same parent index
      newEdhProtocolList[parentIdx].isChecked = value === "true";
      currentParentIdx = parentIdx;
    });

    setPageState({
      ...pageState,
      isEdited: true,
    });

    setSpreadsheetState({
      ...spreadsheetState,
      cpntPjtList: newEdhProtocolList,
    });
  };

  const onCellClicked = (event: CustomEvent) => {
    const eventName: string = event.detail.name;
    const value = event.detail.value;
    if (eventName === EVENT_CLICK_PJT_NAME) {
      const newParams = {
        cpntPjtId: value.cpntPjt.pjtId,
      };
      if (!pageState.isEdited) {
        transition(`/component-project-detail?${stringify(newParams)}`);
        return;
      }
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          transition(`/component-project-detail?${stringify(newParams)}`);
        },
      });
    }
    if (eventName === EVENT_CLICK_PJT_NAME_NEW_TAB_ICON) {
      const newParams = {
        cpntPjtId: value.cpntPjt.pjtId,
      };
      const href = createPath({
        pathname: `/component-project-detail?${stringify(newParams)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
    if (eventName === EVENT_CLICK_CPNT_ID) {
      const newParams = {
        dipByRgn: value.cpntPjt.dipByRgn,
        rootCpntId: value.cpntPjt.rootCpntId,
        cpntId: value.cpntPjt.cpntId,
        godFlg: InDefCommonFlagType.NO,
      };
      if (!pageState.isEdited) {
        transition(`/component-detail?${stringify(newParams)}`);
        return;
      }
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          transition(`/component-detail?${stringify(newParams)}`);
        },
      });
    }
    if (eventName === EVENT_CLICK_CPNT_ID_NEW_TAB_ICON) {
      const newParams = {
        dipByRgn: value.cpntPjt.dipByRgn,
        rootCpntId: value.cpntPjt.rootCpntId,
        cpntId: value.cpntPjt.cpntId,
        godFlg: InDefCommonFlagType.NO,
      };
      const href = createPath({
        pathname: `/component-detail?${stringify(newParams)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  const onClickSearch = () => {
    const search = () => {
      sendComponentProjectsDeployCandidateRequest({
        envType: params.envType,
        dipByRgn: pageState.dipByRegion,
        cpntId: pageState.componentId,
      });
      setPageState((prevState) => ({
        ...prevState,
        isEdited: false,
      }));
    };
    if (!pageState.isEdited) {
      resetUserError();
      search();
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked: () => {
        resetUserError();
        search();
      },
    });
  };

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const inputValue = event.detail.value;
    setPageState((prevState) => ({
      ...prevState,
      [name]: inputValue,
    }));
  };

  return (
    <>
      {deploymentRequestInfomationInputDialogState.isShow && (
        <DeploymentRequestInfomationInputDialogContent />
      )}
      <FrVrfAccordion>
        <FrVrfFormPanel columns={3}>
          <FrVrfSingleLineInputField
            labelContent="DIP By Region"
            content={pageState.dipByRegion}
            onInputValueChanged={onChangeValueHandle("dipByRegion")}
            errorMessage={errorMessages(pageState.fieldPath.dipByRgn)}
          />
          <FrVrfSingleLineInputField
            labelContent="Component ID (partial match) "
            content={pageState.componentId}
            onInputValueChanged={onChangeValueHandle("componentId")}
            errorMessage={errorMessages(pageState.fieldPath.cpntId)}
          />
          <div style={{ textAlign: "center" }}>
            <FrVrfButton
              content="Search"
              theme="primary"
              size="large"
              onClicked={onClickSearch}
            />
          </div>
        </FrVrfFormPanel>
      </FrVrfAccordion>
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={COMPONENT_PROJECT_LIST_REQUEST_TEMPLATE_FILE_URL}
        onInitialized={initSpreadsheetState}
        onCellEdited={onCellEdited}
        onCellClicked={onCellClicked}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={800}
      />
      <FrVrfFooter>
        <FrVrfButton
          content="Back"
          theme="secondary"
          size="large"
          slot="left"
          onClicked={transitionHandle}
          authActId="component_project_list_request.button.cancel"
        />
        <FrVrfButton
          content="Re-enter Attributes"
          theme="primary"
          size="large"
          onClicked={onClickReEnterAttributes}
          authActId="component_project_list_request.button.re_enter_attributes"
          disabled={!pageState.isEdited}
        />
        <FrVrfButton
          content="Confirm Deployment Request"
          theme="primary"
          size="large"
          onClicked={onClickConfirmDeploymentRequest}
          authActId="component_project_list_request.button.confirm_deploy_request"
          disabled={!pageState.isEdited}
        />
      </FrVrfFooter>
    </>
  );
};
